import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import IsoDatePicker from '../../schedule/components/IsoDatePicker'
import MoneyInput from '../components/MoneyInput'
import { emptyReleaseForm, releaseFormProblem, type ReleaseForm } from '../lib/applications'
import { todayIso } from '../lib/invoices'
import { formatMoney } from '../lib/money'

interface Props {
  open: boolean
  remaining: string
  currency: string
  locale: string
  onOpenChange: (open: boolean) => void
  onSubmit: (input: Record<string, unknown>) => Promise<boolean>
}

/** Records a retention release (1b-A spec §5): a date, an amount up to what remains, a note. */
const RetentionReleaseDialog: React.FC<Props> = ({ open, remaining, currency, locale, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<ReleaseForm>(() => emptyReleaseForm(todayIso()))
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  useEffect(() => {
    if (!open) return
    setForm({ ...emptyReleaseForm(todayIso()), amount: remaining })
    setTouched(false)
    setSubmitting(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  const problem = releaseFormProblem(form, remaining)
  const submit = async () => {
    setTouched(true)
    if (problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit({ date: form.date, amount: form.amount, note: form.note.trim() || null })
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('CostControl_retention_release_title')}</DialogTitle>
          <DialogDescription>{t('CostControl_retention_release_hint', { amount: formatMoney(remaining, { locale, currency }) })}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); submit() }}>
          <div className="space-y-1.5">
            <Label htmlFor="cc-rel-date">{t('CostControl_retention_field_date')} *</Label>
            <IsoDatePicker id="cc-rel-date" value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-rel-amount">{`${t('CostControl_retention_field_amount')} (${currency})`} *</Label>
            <MoneyInput id="cc-rel-amount" value={form.amount} onChange={(value, valid) => setForm((f) => ({ ...f, amount: value, amountValid: valid }))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-rel-note">{t('CostControl_retention_field_note')}</Label>
            <Input id="cc-rel-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
          </div>
          {touched && problem && <p className="text-sm text-destructive">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('CostControl_retention_release')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default RetentionReleaseDialog
